import { useEffect, useMemo, useRef, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { separateLandingPageDisclaimer } from "@/lib/landing-page-disclaimer";
import { LP_CHROME_CSS } from "@/lib/lp-chrome-css";
import { notifyForm } from "@/lib/notifyForm";
import { PILES_HOSPITAL_CSS, PILES_HOSPITAL_HTML } from "@/lib/piles-hospital-lp";
import { PILES_TREATMENT_CITIES, type PilesTreatmentCity } from "@/components/PilesTreatmentCityPage";

const PILES_HOSPITAL_FIXES = `
.lp-piles-hospital{--navy:#1b3358;--navy-dark:#1a1930;--navy-soft:#4e5072;--slate:#4e5072;--coral:#ff5757;--coral-dark:#e54848;--rose:#e3a1a0;--rose-soft:#f4d4d3;--white:#ffffff;--paper:#fafbfc;--paper-2:#f1f2f6;--line:#e1e3ea;--ink:#1a1930;--ink-soft:#4e5072;--mute:#7a7e92;--ok:#1f7a5c;--wa-green:#25d366;--r-sm:8px;--r-md:14px;--r-lg:22px;--shadow-sm:0 2px 4px rgba(27,51,88,.06),0 1px 2px rgba(27,51,88,.04);--shadow-md:0 8px 24px rgba(27,51,88,.1),0 2px 6px rgba(27,51,88,.06);--shadow-lg:0 16px 40px rgba(27,51,88,.12)}
.lp-piles-hospital .btn-call{background:var(--coral);color:var(--white);box-shadow:0 4px 14px rgba(255,87,87,.32)}
.lp-piles-hospital .hero .btn-primary{background:var(--coral);color:var(--white);box-shadow:0 4px 14px rgba(255,87,87,.32)}
.lp-piles-hospital .hero-visual .doctor-tag,.lp-piles-hospital .hero-visual .doctor-tag .role,.lp-piles-hospital .hero-visual .doctor-tag .name,.lp-piles-hospital .hero-visual .doctor-tag .qual{color:var(--white)}
.lp-piles-hospital[data-cta-mode="active"] .cta-off-only{display:none!important}
.lp-piles-hospital[data-cta-mode="off"] .cta-active-only{display:none!important}
.lp-piles-hospital[data-cta-mode="off"] .wa-bubble{display:none!important}
@media(min-width:900px){.lp-piles-hospital .bottom-bar{display:none!important}}
`;

function istHour() {
  try {
    return Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", hour12: false }));
  } catch {
    return 12;
  }
}

function renderHospitalHtml(city: PilesTreatmentCity) {
  const config = PILES_TREATMENT_CITIES[city];
  const values: Record<string, string> = {
    CITY: city,
    DOCTOR_NAME: config.doctor,
    DOCTOR_IMAGE: config.doctorImage,
    DOCTOR_ROLE: config.doctorRole,
    DOCTOR_SHORT_QUALS: config.doctorShortQuals,
    ADDRESS: config.address,
    ADDRESS_SHORT: config.addressShort,
    MAP_URL: config.mapUrl.replaceAll("&", "&amp;"),
  };
  return Object.entries(values).reduce((html, [key, value]) => html.replaceAll(`{{${key}}}`, value), PILES_HOSPITAL_HTML);
}

export function PilesHospitalCityPage({ city }: { city: PilesTreatmentCity }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [ctaMode, setCtaMode] = useState<"active" | "off">("active");
  const config = PILES_TREATMENT_CITIES[city];
  const html = useMemo(() => separateLandingPageDisclaimer(renderHospitalHtml(city)), [city]);

  useEffect(() => setCtaMode(istHour() >= 9 && istHour() < 20 ? "active" : "off"), []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];
    const leadModal = root.querySelector<HTMLElement>("#lead-modal");
    const callModal = root.querySelector<HTMLElement>("#call-modal");
    const formContainer = root.querySelector<HTMLElement>("#modal-form-container");
    const thankYou = root.querySelector<HTMLElement>("#thank-you");
    const openModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.add("open");
      modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("modal-open");
    };
    const closeModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.remove("open");
      modal.setAttribute("aria-hidden", "true");
      if (!root.querySelector(".modal-backdrop.open")) document.documentElement.classList.remove("modal-open");
    };
    const openLeadForm = (doctor = "") => {
      const doctorField = root.querySelector<HTMLInputElement>("#m-doctor");
      const pageField = root.querySelector<HTMLInputElement>("#m-pageurl");
      if (doctorField) doctorField.value = doctor;
      if (pageField) pageField.value = window.location.href;
      if (formContainer) formContainer.style.display = "";
      thankYou?.classList.remove("active");
      openModal(leadModal);
    };
    const clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const close = target?.closest("[data-modal-close]");
      if (close) { event.preventDefault(); closeModal(close.closest(".modal-backdrop") as HTMLElement | null); return; }
      const opener = target?.closest("[data-open-form]");
      if (opener) { event.preventDefault(); openLeadForm(opener.getAttribute("data-doctor") || ""); return; }
      const insurance = target?.closest("#insurance-check-btn");
      if (insurance) { event.preventDefault(); openLeadForm(); }
    };
    root.addEventListener("click", clickHandler);
    cleanups.push(() => root.removeEventListener("click", clickHandler));
    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      const handler = (event: Event) => {
        if (root.dataset.ctaMode === "off") { event.preventDefault(); openLeadForm(); }
        else if (window.matchMedia("(min-width: 900px) and (pointer: fine)").matches) { event.preventDefault(); openModal(callModal); }
      };
      link.addEventListener("click", handler);
      cleanups.push(() => link.removeEventListener("click", handler));
    });
    root.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((backdrop) => {
      const handler = (event: Event) => { if (event.target === backdrop) closeModal(backdrop); };
      backdrop.addEventListener("click", handler);
      cleanups.push(() => backdrop.removeEventListener("click", handler));
    });
    const keyHandler = (event: KeyboardEvent) => { if (event.key === "Escape") root.querySelectorAll<HTMLElement>(".modal-backdrop.open").forEach(closeModal); };
    document.addEventListener("keydown", keyHandler);
    cleanups.push(() => document.removeEventListener("keydown", keyHandler));
    root.querySelectorAll<HTMLFormElement>("form.inline-form-right, #modal-form").forEach((form) => {
      const handler = (event: Event) => {
        event.preventDefault();
        const data = new FormData(form);
        const name = String(data.get("name") ?? "").trim();
        void notifyForm({ formType: "Piles Hospital Consultation", source: `${city} Piles Hospital Page`, fields: [
          { label: "Name", value: name }, { label: "Phone", value: String(data.get("phone") ?? "").trim() },
          { label: "Treatment", value: "Piles Care" }, { label: "City", value: city },
          { label: "Doctor", value: String(data.get("doctor") ?? config.doctor) }, { label: "Page", value: window.location.href },
        ] });
        const thankYouName = root.querySelector("#ty-name");
        if (thankYouName) thankYouName.textContent = (name || "friend").split(" ")[0];
        form.reset(); if (formContainer) formContainer.style.display = "none"; thankYou?.classList.add("active"); openModal(leadModal);
      };
      form.addEventListener("submit", handler);
      cleanups.push(() => form.removeEventListener("submit", handler));
    });
    const mapFrame = root.querySelector<HTMLElement>("#map-frame");
    if (mapFrame && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || mapFrame.querySelector("iframe")) return;
        const src = mapFrame.dataset.mapSrc; if (!src) return;
        const iframe = document.createElement("iframe"); iframe.src = src; iframe.loading = "lazy";
        iframe.title = `Healic Multispeciality Hospital ${city} map`; iframe.referrerPolicy = "no-referrer-when-downgrade";
        mapFrame.appendChild(iframe); observer.disconnect();
      }, { rootMargin: "300px" });
      observer.observe(mapFrame); cleanups.push(() => observer.disconnect());
    }
    return () => { cleanups.forEach((cleanup) => cleanup()); document.documentElement.classList.remove("modal-open"); };
  }, [city, config.doctor]);

  return <>
    <style dangerouslySetInnerHTML={{ __html: PILES_HOSPITAL_CSS + LP_CHROME_CSS + PILES_HOSPITAL_FIXES }} />
    <div className="lp-chrome"><SiteHeader /></div>
    <div ref={rootRef} className="lp-piles-hospital" data-cta-mode={ctaMode} dangerouslySetInnerHTML={{ __html: html.contentHtml }} />
    <div className="lp-chrome"><SiteFooter /></div>
    <section className="lp-piles-hospital lp-disclaimer-wrap"><div className="wrap"><div className="disclaimer">Results vary by individual. Piles treatment and surgery carry risks that your surgeon will explain during consultation. This page is educational and is not a substitute for medical advice.</div></div></section>
  </>;
}
